import type { Image } from "./types";

// Cloudflare Images delivery. Every Lecturehub image URL is built client-side
// (here, server-side actually — we resolve URLs in server components) from two
// values: the per-tenant `Portal.cloudflareAccountHash` and the per-image
// `Image.hash`. See docs/synced/images/README.md for the full contract.

export type ImageVariant = "thumbnail" | "public" | "xl" | "xxl";

// Shared by every Cloudflare account — not per-tenant, so it stays a constant
// here rather than in env/portal config. Also allow-listed in next.config.ts.
const CF_HOST = "https://imagedelivery.net";

/**
 * Resolve an Image node to a renderable URL.
 *
 * - When the tenant has Cloudflare Images configured (`cloudflareAccountHash`
 *   set) AND the image carries a `hash`, returns the CF delivery URL for the
 *   requested variant.
 * - Otherwise falls back to `Image.uri`, which the backend always resolves to a
 *   plain URL. So an un-configured tenant, or an image whose `hash` wasn't
 *   selected in the query, still renders.
 * - Returns `null` only when there is no image at all — callers should render a
 *   placeholder rather than an empty `src`.
 */
export function buildImageUrl(
  image: Pick<Image, "hash" | "uri"> | null | undefined,
  variant: ImageVariant,
  cloudflareAccountHash: string | null,
): string | null {
  if (!image) return null;
  if (cloudflareAccountHash && image.hash) {
    return `${CF_HOST}/${cloudflareAccountHash}/${image.hash}/${variant}`;
  }
  return image.uri ?? null;
}
